export interface FlowGenBasic {
  currentSequence?: number;
  dateCreated?: string;
  dateTutorReassigned?: string;
  firstName?: string;
  flowGeneratorSessionId?: number;
  flowItemGroupInputs?: [];
  isAutoModerated?: boolean;
  isProactiveIntervention?: boolean;
  isAutoResponseEnabled?: boolean;
  lastName?: string;
  lessonId?: number;
  lessonProgress?: [];
  lessonType?: string;
  percentageComplete?: number;
  percentages?: [];
  position?: number;
  previousTutorId?: number;
  progress?: [];
  quizQuestionCorrectness?: {};
  templateSlug?: string;
  tutorFirstName?: string;
  tutorId?: number;
  tutorUserId?: number;
  userId?: number;
  yearGroup?: string;
}

export interface MetaData {
  firstName: string;
  lastName: string;
  yearGroup: string;
  quizName: string;
  quizId: number;
  lessonType: string;
  assignmentId: number;
  avgScore: number;
  percentageComplete: number;
  country: string;
  isProactiveIntervention: boolean;
  recentQuestionSequence: number;
  recentCurrentQuestionId: number;
  recentQuestionAnswerValue: number;
  recentQuestionAnswerCorrect: boolean;
  recentQuestionAnswer: string;
  recentQuestionMetadata: string;
  prompt: string;
  quizQuestionCorrectness: {
    [key: string]: number;
  };
  percentages: {
    sequence: number;
    value: number;
    color: string;
  }[];
}

export interface Parent {
  userId: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  subscriptionStatus: string;
}

export interface TopicInsight {
  userId: number;
  topicPathwayCollectionStudentId: number;
  quizSessionId: number;
  dateCreated: string; // ISO date string
  dateCompleted: string; // ISO date string
  topicPathwayQuizId: number;
  quizId: number;
  level: number;
  questionCount: number;
  correctAnswerCount: number;
  incorrectAnswerCount: number;
  answerCount: number;
  correctionCorrectAnswerCount: number;
  correctionIncorrectAnswerCount: number;
  correctionAnswerCount: number;
}

export interface ProgressLevel {
  topicPathwayCollectionStudentId: number;
  isStartingLevel: boolean;
  level: number;
  completedQuizCount: number;
  startedQuizCount: number;
  quizCount: number;
}

export interface StudentProgress {
  previousLevel: number;
  currentLevel: number;
  nextLevel: number;
  levelProgress: ProgressLevel[];
}

export interface DynamicQuizSummary {
  userId: number;
  topicPathwayCollectionStudentId: number;
  dynamicQuizAnswerPattern: string;
  quizSessionId: number;
}

export interface Student {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  dateOfBirth: string; // ISO date string
  dateLastLoggedIn: string; // ISO date string
  dateCreated: string; // ISO date string
  yearGroupName: string;
  uniqueCode: string;
  schoolId: number;
  topicPathwayCollectionStudentId: number;
  topicPathwayCollectionId: number;
  studentId: number;
  currentLevel: number;
  startingLevel: number;
  dynamicQuizQuizSessionId: number;
  isOnline: boolean;
  dynamicQuizSummary: DynamicQuizSummary;
  progress: StudentProgress;
  parents: Parent[];
  topicInsights: TopicInsight[];
}

export interface FlowItemGroupInput {
  flowItemGroupInputId: number;
  flowGeneratorSessionId: number;
  flowItemGroup: string;
  position: number;
  input: string;
  isInputProcessed: boolean;
  flowItemGroupType: string;
  contentType: string;
  dateCreated: string; // ISO date string
  dateInput: string; // ISO date string
  isValidatorSuccess: boolean;
  userId: number;
  rowVersionString: string;
}

export interface RadioButton {
  sequence: number;
  value: string;
  label: string;
  labelSeed: number;
  labelOptions: string[];
}

export interface AnnotationMisconception {
  misconceptionTagId: number;
  questionId: number;
  answerValue: number;
  embeddingOutputId: number;
  userId: number;
  userName: string;
  dateCreated: string; // ISO date string
  dateUpdated: string; // ISO date string
  misconceptionStatus: string;
  comments: string;
  searchText: string;
  similarDistractorQuestionId: number;
  similarDistractorAnswerValue: number;
  isDeclarativeKnowledge: boolean;
}

export interface Annotation {
  flowGeneratorSessionId: number;
  flowItemId: string;
  dateCreated: string; // ISO date string
  comments: string;
  categories: string[];
  misconception: AnnotationMisconception;
  taggedBy: string;
  misconceptionCertainty: number;
}

export enum FlowItemType {
  Text = "Text",
  Image = "Image",
  Video = "Video",
  RadioButtonInput = "RadioButtonInput",
  TextInput = "TextInput",
  TextInputExplanation = "TextInputExplanation",
  UploadInput = "UploadInput",
  Divider = "Divider",
  File = "File",
  HighlightedText = "HighlightedText",
  Information = "Information",
  DiagnosticQuestionAnswerInput = "DiagnosticQuestionAnswerInput",
  DiagnosticQuestionRetryInput = "DiagnosticQuestionRetryInput",
  PinnedImage = "PinnedImage",
  PinnedVideo = "PinnedVideo",
  PinnedText = "PinnedText",
  Metadata = "Metadata",
  AutoRequestForHelp = "AutoRequestForHelp",
  Default = "Default",
}

export interface FlowItem {
  flowGeneratorSessionId: number;
  templateSlug: string;
  flowItemId: string;
  userId: number;
  isRead: boolean;
  flowItemGroup: string;
  position: number;
  dateCreated: string; // ISO date string
  dateDispatched: string; // ISO date string
  sequence: number;
  text: string;
  textOptions: string[];
  imageUrl: string;
  imageUrlOptions: string[];
  videoUrl: string;
  videoUrlOptions: string[];
  fileUrl: string;
  radioButtons: RadioButton[];
  flowItemType: FlowItemType | string;
  milliSecondsDelay: number;
  intentName: string;
  isSentimentResponse: boolean;
  isNextExpression: string;
  isSuccessExpression: string;
  isAwaitingInput: boolean;
  isParamsAllMatched: boolean;
  annotation: Annotation;
}

export interface SubFlowGeneratorSession {
  id: number;
  flowGeneratorSessionId: number;
  lessonId: number;
  lessonType: string;
  templateSlug: string;
  userId: number;
  position: number;
  totalPositions: number;
  currentSequence: number;
  isLocked: boolean;
  dateCreated: string; // ISO date string
  dateDeleted: string; // ISO date string
  dateCompleted: string; // ISO date string
  flowItemGroupInputs: FlowItemGroupInput[];
  flowItems: FlowItem[];
  rowVersionString: string;
}

export interface QuizSession {
  quizId: number;
  quizName: string;
  topicPathwayCollectionStudentId: number;
  topicPathwayQuizId: number;
  quizSessionId: number;
  dateCompleted: string; // ISO date string
  dateCreated: string; // ISO date string
  correctAnswerCount: number;
  incorrectAnswerCount: number;
  answerCount: number;
  questionCount: number;
  quizLevel: number;
}

export interface Percentage {
  sequence: number;
  value: number;
  color: string;
}

export interface FlowGeneratorSession {
  id: number;
  lessonId: number;
  questionId: number | null;
  quizId: number | null;
  quizSessionId: number | null;
  lessonType: string | null;
  dateCreated: string | null; // ISO date string
  conversationId: string | null;
  dateHelpRequested: string | null; // ISO date string
  userId: number;
  quizName: string | null;
  firstName: string | null;
  lastName: string | null;
  yearGroup: string | null;
  dateCompleted: string; // ISO date string
  flowSessionId: string;
  flowGeneratorSessionId: number;
  assignmentId: number | null;
  lessonRating: string | null;
  lessonConfidence: string | null;
  questionSequence: number | null;
  isOnline: boolean;
  isHelpRequested: boolean;
  isInProgress: boolean;
  tutorId: number | null;
  tutorUserId: number | null;
  tutorUsername: string | null;
  // Safety-pause shape (see isPausedForReview): isAutoModerated=true + no
  // current tutor + the AI tutor as previousTutorId = paused for review.
  // Optional — older payloads omit them.
  isAutoModerated?: boolean | null;
  previousTutorId?: number | null;
  isAutoResponseEnabled?: boolean | null;
  botJoined?: boolean | null;
  // Nested intervention rows (camelCase, unlike the PascalCase SignalR push
  // shape FlowGeneratorSessionIntervention). The latest open row (dateEnded
  // null, highest id) tells a round-robin-ASSIGNED safety review apart from a
  // claimed chat — see isHeldAssignedToMe. Optional — older payloads omit it.
  flowGeneratorSessionInterventions?: Array<FlowGeneratorSessionInterventionRow> | null;
  isProactiveIntervention: boolean | null;
  isGPTEnabled: boolean;
  avgScore: number | null;
  percentageComplete: number | null;
  dateProcessed: string | null;
  country: string | null;
  annotationsCount: number | null;
  metaData: MetaData | null;
  student: Student | null;
  flowGeneratorSession: SubFlowGeneratorSession;
  quizSession: QuizSession;
  quizQuestionCorrectness: {
    [key: string]: number;
  };
  percentages: Percentage[];
}

/**
 * Nested intervention row inside GET /v4/admin/flow-generator-sessions/{id}.
 * Only the fields the safety-review gating reads are typed. NOTE previousTutorId
 * and tutorId here are TUTOR ids (compare via isAiTutor / tutorId), never user ids.
 */
export interface FlowGeneratorSessionInterventionRow {
  flowGeneratorSessionInterventionId: number;
  tutorId?: number | null;
  dateEnded?: string | null;
  isAutoModerated?: boolean | null;
  // AI auto-send: false while a safety hold keeps the AI paused; resuming or
  // claiming sets it true server-side.
  isAutoResponseEnabled?: boolean | null;
  botJoined?: boolean | null;
  previousTutorId?: number | null;
}

/**
 * Response of GET .../interventions/handback-tutor (see getFlowHandbackTutor).
 * No longer carried on session payloads or SignalR pushes.
 */
export interface HandbackTutor {
  tutorId: number;
  firstName: string;
  // Point-in-time: false = EEDI's AI tutor is not on duty right now (no
  // active rota / offline / at capacity) — button shows but disabled. The
  // backend enforces this independently, so a stale enabled button just gets
  // a clean error.
  isAvailable: boolean;
}

export interface FlowGeneratorSessionInterventionMessage {
  FlowGeneratorSessionInterventionMessageId: number;
  FlowGeneratorSessionInterventionId: number;
  FlowGeneratorSessionId: number;
  UserId: number;
  DateCreated: Date;
  DateDispatched?: Date;
  Message: string;
  RowKey: string;
  Position?: number;
  Source: MessageSource;
  MessageRejected?: boolean;
  MessageRejectedReason: string;
  VideoUrl: string;
  ImageUrl: string;
  FileUrl: string;
  FlowItemType: FlowItemType;
  Metadata: string;
}

export enum MessageSource {
  User,
  Tutor,
  Bot,
}

export interface FlowGeneratorSessionIntervention {
  FlowGeneratorSessionInterventionId: number;
  FlowGeneratorSessionId: number;
  TutorId?: number;
  DateTutorJoined?: string;
  StudentResponded?: boolean;
  IsFlaggedAsOffensive?: boolean;
  TutorSatisfactionRating?: number;
  UserSatisfactionRating?: number;
  TutorNotes?: string;
  BotJoined?: string;
  BotFailed?: string;
  BotPrompt?: string;
  DateEnded?: string;
  Metadata?: string;
  BotModelId?: string;
  IsProactiveIntervention?: boolean;
  MisconceptionResolutionRating?: number;
  BotSatisfactionRating?: number;
  SocrateRating?: number;
  SocratesSessionID?: string;
  ExperimentArm?: string;
  // True = auto-moderated session served directly by the AI tutor proxy
  // (responses dispatch straight to the student, no human vetting).
  IsAutoModerated?: boolean;
}

export interface FlowGeneratorSessionInterventionUpsert {
  TutorId?: number;
  DateTutorJoined?: string;
  StudentResponded?: boolean;
  IsFlaggedAsOffensive?: boolean;
  TutorSatisfactionRating?: number;
  UserSatisfactionRating?: number;
  TutorNotes?: string;
  BotJoined?: string;
  BotFailed?: string;
  BotPrompt?: string;
  DateEnded?: string;
  Metadata?: string;
  BotModelId?: string;
  IsProactiveIntervention?: boolean;
  MisconceptionResolutionRating?: number;
  BotSatisfactionRating?: number;
  SocratesSessionID?: string;
  ExperimentArm?: string;
}
